import { Conditional } from '@angular/compiler';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, Observable, merge, of } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';
import { IDataTableRowActionClickArg } from 'src/app/share/components/data-table-card/data-table-card.component';
import { IDataPanelViewModel, IRowViewModel } from 'src/app/share/entities/view-model';
import { FormInputComponent, IFormInputViewModel } from 'src/app/share/forms/form-input/form-input.component';
import { FormOkCancelComponent, IFormOkCancelViewModel } from 'src/app/share/forms/form-ok-cancel/form-ok-cancel.component';
import { BasePageComponent, IBasePageOnInitArg } from 'src/app/share/pages/base-page.component';
import { IPageContentViewModel, IPageDataViewModel, IPageViewModel } from 'src/app/share/pages/pages';
import { AuthService } from 'src/app/share/services/auth.service';
import { CommonService } from 'src/app/share/services/common.service';
import { DateUtil } from 'src/app/share/utils/date-util';
import { ShellService } from 'src/app/shell/shell.service';

@Component({
  selector: 'app-affiliate-payout',
  templateUrl: './affiliate-payout.component.html',
  styleUrls: ['./affiliate-payout.component.scss']
})
export class AffiliatePayoutComponent extends BasePageComponent implements OnInit {

  private readonly apiPath: string = '/api/costing';

  vm$: Observable<IViewModel>;
  searchData: string = '';

  private pageCommandBs$: BehaviorSubject<string> = new BehaviorSubject<string>('initialize');

  constructor(private commonService: CommonService, private authService: AuthService, protected shellService: ShellService, protected activatedRate: ActivatedRoute) { 
    super(shellService, activatedRate);
  }
  
  ngOnInit(): void {

    var today = new Date();

    const loader$: Observable<IViewModel> = of({
      filterData:  {
        fromDate: moment(today).format('YYYY-MM-DD'),
        toDate: moment(today).format('YYYY-MM-DD')
      }
    }).pipe(
      switchMap(ctx => this.commandInvoke(ctx.filterData, 'page-load').pipe(
        map((rvm: IDataViewModel) => {
          return {
            ...ctx,
            dataVm: rvm
          };
        })
      )),
      map(ctx => { 

        const ret: IViewModel = {
          actionsAllowed: true, canExportExcel: false,
          filterVm: {
            data: ctx.filterData,
            fromDateInputVm: FormInputComponent.viewModel(ctx.filterData, 'fromDate',
                      { title: 'From Date', isRequired: false, type: 'date' }),
            toDateInputVm: FormInputComponent.viewModel(ctx.filterData, 'toDate',
                      { title: 'To Date', isRequired: false, type: 'date' })
          },
          dataVm: ctx.dataVm
        }
        
        return ret;
      })
    );

    // commands
    const comand$: Observable<IViewModel> = this.pageCommandBs$.pipe(
      switchMap(cmdText => { 
        const ret: IViewModel = {
          actionsAllowed: false, canExportExcel: false, 
          filterVm: undefined,
          dataVm: {
            workingText: cmdText == 'initialize' ? 'Initializing...' : cmdText == 'refresh' ? 'Refreshing...' : 'Loading...',
            errorText: undefined,
            payoutDataVm: undefined
          }
        };

        if (cmdText == 'refresh')
          return merge(of(ret), loader$);
        else
          return of(ret);
      })
    );

    this.vm$ = merge(comand$, loader$);
    
  }

  private commandInvoke(filterData: any, action: string): Observable<IDataViewModel> {
    return of({}).pipe(
      switchMap(ctx => this.commonService.getData('PageCommand', 'affiliate-payout', action, filterData, this.apiPath, true)),
      map((v: IDataViewModel) => {
        if (v.workingText)
          return { ...v };
        const ret = { ...v, actionsAllowed: true };
        if (ret.data) {

            ret.payoutDataVm = {
              data: { 
                columns: [ "Affiliate Number", "Affiliate Name", "Confirmation", "Date", "Time", "Base Amount", "Payout Amount" ],
                unauthorized: false,
                rows: ret.data.payoutData.map(r => [ r.affiliateCode, r.affiliateName, r.confirmationNumber, r.pickupDate, r.pickupTime, r.baseCostAmountString, r.payoutAmountString ]),
              },
              rows: this.loadPayoutRows(ret.data.payoutData),
              rowsFiltered: this.loadPayoutRows(ret.data.payoutData)
            }
            ret.lastDateTimeUpdated = new Date().toLocaleDateString();  
          }
        return ret;
      })
    );
  }


  loadPayoutRows(data: IPayoutDatatModel[]): IRowViewModel[] {
    var ret: IRowViewModel[] = [];

    data.forEach(r => {
      var goodRec: boolean = true;
      // add filter here if need
      if (goodRec) {
        ret.push({
          code: r.confirmationNumber, 
          fields: [ r.affiliateCode, r.affiliateName, r.confirmationNumber, r.pickupDate, r.pickupTime, r.baseCostAmountString, r.payoutAmountString ], 
          actions: [ { code: 'ignore', name: 'Ignore' } ]
        });
      }
    });
    return ret;
  }

  /**
   * 
   * @param pvm 
   * @param vm
   * @param tvm
   * @param arg 
   */
  rowActionInvoke(arg: IDataTableRowActionClickArg, vm: IViewModel) {
    vm.confirmFormVm = this.createConfirmFormVm(arg.rowCode);
  }

  createConfirmFormVm(confirmationNumber: string): IConfirmEntryFormViewModel {
    const data = { reason: ''};

    return {
      confirmationNumber: confirmationNumber,
      message: `Are you sure you want to ignore the confirmation ${confirmationNumber}?`,
      okCancelVm: FormOkCancelComponent.okCancelVm({}),
      reasonInputVm: FormInputComponent.viewModel(data, 'reason', { title: 'Please enter the reason for ignoring payout', isRequired: false, isLocked: false })
    };
  }

  async buttonClicked(fvm: IFilterData, vm: IViewModel) {
    
    try {
      vm.dataVm.workingText = 'Loading...';
      vm.dataVm.errorText = undefined;

      var result = await of({}).pipe(
        delay(500),
        switchMap(ctx => this.commandInvoke(fvm, 'page-load'))
      ).toPromise();

      vm.dataVm = result;

    } catch (err) {
      vm.dataVm.errorText = err;
    } finally {
      vm.dataVm.workingText = undefined;
    }
  }

  confirmFormCanceled(vm: IViewModel) {
    vm.confirmFormVm = undefined;
  }

  async confirmFormOk(vm: IViewModel) {
    try {
      const data = vm.dataVm.data.payoutData.find(i => i.confirmationNumber == vm.confirmFormVm.confirmationNumber);
      if (data)
        data.comments = vm.confirmFormVm.reasonInputVm.dataGet();

      vm.confirmFormVm = undefined;
      vm.dataVm.workingText = 'Updating...';
      vm.dataVm.errorText = undefined;

      var result = await of({}).pipe(
        delay(500),
        switchMap(ctx => this.commandInvoke({ payoutData: data, fromDate: vm.filterVm.data.fromDate, toDate: vm.filterVm.data.toDate }, 'ignore-reservation'))
      ).toPromise();

      vm.dataVm = result;

    } catch (err) {
      vm.dataVm.errorText = err;
    } finally {
      vm.dataVm.workingText = undefined;
    }
  }

  doDataSearch(vm: IViewModel, searchVal: string) {
    if (searchVal.length === 0) {
      vm.dataVm.payoutDataVm.rowsFiltered = vm.dataVm.payoutDataVm.rows;
      return;
    }

    vm.dataVm.payoutDataVm.rowsFiltered = [];
    vm.dataVm.payoutDataVm.rows.forEach(r => {
      var notFound: boolean = true;
      r.fields.forEach(c => {
        if (c.toLowerCase().indexOf(searchVal.toLowerCase()) > -1)
          notFound = false;
      })

      if (!notFound) {
          vm.dataVm.payoutDataVm.rowsFiltered.push(r);
      }
    });
  }
}

/**
 * 
 */
interface IViewModel extends IPageDataViewModel {

  dataVm?: IDataViewModel;

  confirmFormVm?: IConfirmEntryFormViewModel;

  filterVm?: {
    data: IFilterData;
    fromDateInputVm?: IFormInputViewModel;
    toDateInputVm?: IFormInputViewModel;
  }

  isValid?: boolean;
}

/**
 * 
 */
interface IConfirmEntryFormViewModel {
  reason?: string;
  confirmationNumber: string;
  message: string;
  okCancelVm?: IFormOkCancelViewModel;
  reasonInputVm: IFormInputViewModel;
}

/**
 * 
 */
interface IFilterData {
  fromDate?: string;
  toDate?: string;
}

/**
 * 
 */
interface IDataViewModel {

  data?: { payoutData: IPayoutDatatModel[]; }
  
  payoutDataVm?: IDataPanelViewModel;
  
  lastDateTimeUpdated?: string;
  workingText?: string;
  errorText?: string;
}

/**
 * 
 */
interface IPayoutDatatModel {
  affiliateCode: string;
  affiliateName: string;
  confirmationNumber: string;
  pickupDate: string;
  pickupTime: string;
  baseCostAmount: number;
  payoutAmount: number;
  comments: string;
  baseCostAmountString: string;
  payoutAmountString: string;
}

/**
 * 
 */
interface IDataModel {
  payoutData: IPayoutDatatModel[];
  filterData: IFilterData;
}